import { Typography, Tooltip, Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { formatDate } from '@/utils';
import { osirisTokens } from '@/theme/tokens';

/**
 * Hover-only row actions — the shared pattern for all tables. Actions sit in
 * their own cell, so the group has no backdrop plate of its own.
 */
export function TableRowActions({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <Box
      className={`table-row-actions${className ? ' ' + className : ''}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '1px',
      }}
    >
      {children}
    </Box>
  );
}

const NAME_HOVER_COLOR = '#c4b5fd';

interface TableCellTextProps {
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * Standard table cell text with consistent styling
 */
export function TableCellText({ children, sx }: TableCellTextProps) {
  return <Typography sx={{ fontWeight: 400, fontSize: '13px', ...sx }}>{children}</Typography>;
}

interface TableCellClickableProps {
  children: React.ReactNode;
  onClick: () => void;
  title?: string;
  sx?: SxProps<Theme>;
}

/**
 * Clickable table cell text with hover underline
 */
export function TableCellClickable({ children, onClick, title, sx }: TableCellClickableProps) {
  return (
    <Typography
      className="table-cell-clickable"
      sx={{
        fontFamily: osirisTokens.fonts.body,
        fontWeight: 400,
        fontSize: '13px',
        letterSpacing: '0.02em',
        color: osirisTokens.text.primary,
        textDecoration: 'underline',
        textUnderlineOffset: '3px',
        cursor: 'pointer',
        '&:hover': {
          color: NAME_HOVER_COLOR,
        },
        ...sx,
      }}
      title={title}
      onClick={onClick}
    >
      {children}
    </Typography>
  );
}

interface TableCellDateProps {
  date: string | Date;
  onClick?: () => void;
  tooltipText?: string;
}

/**
 * Formatted date cell with optional click handler and tooltip
 */
export function TableCellDate({ date, onClick, tooltipText }: TableCellDateProps) {
  const formattedDate = formatDate(date);

  const content = onClick ? (
    <TableCellClickable onClick={onClick}>{formattedDate}</TableCellClickable>
  ) : (
    <TableCellText>{formattedDate}</TableCellText>
  );

  if (tooltipText) {
    return <Tooltip title={tooltipText}>{content}</Tooltip>;
  }

  return content;
}

interface TableCellTruncatedProps {
  text: string | null | undefined;
  maxLength?: number;
  placeholder?: string;
  onClick?: () => void;
}

/**
 * Truncated text cell with tooltip showing full text
 */
export function TableCellTruncated({
  text,
  maxLength = 50,
  placeholder = '—',
  onClick,
}: TableCellTruncatedProps) {
  if (!text) {
    return <TableCellText sx={{ color: 'text.secondary' }}>{placeholder}</TableCellText>;
  }

  const needsTruncation = text.length > maxLength;
  const displayText = needsTruncation ? `${text.substring(0, maxLength)}...` : text;

  if (onClick) {
    const content = (
      <TableCellClickable onClick={onClick} title={needsTruncation ? text : undefined}>
        {displayText}
      </TableCellClickable>
    );

    return needsTruncation ? <Tooltip title={text}>{content}</Tooltip> : content;
  }

  return needsTruncation ? (
    <Tooltip title={text}>
      <span>
        <TableCellText>{displayText}</TableCellText>
      </span>
    </Tooltip>
  ) : (
    <TableCellText>{displayText}</TableCellText>
  );
}

interface TableCellMultiLineProps {
  text: string | null | undefined;
  maxLines?: number;
  placeholder?: string;
  onClick?: () => void;
}

/**
 * Multi-line text cell that truncates with ellipsis after specified lines
 * Shows full text in tooltip on hover
 */
export function TableCellMultiLine({
  text,
  maxLines = 2,
  placeholder = '—',
  onClick,
}: TableCellMultiLineProps) {
  if (!text) {
    return <TableCellText sx={{ color: 'text.secondary' }}>{placeholder}</TableCellText>;
  }

  const textStyle = {
    fontWeight: 400,
    fontSize: '13px',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: maxLines,
    WebkitBoxOrient: 'vertical',
  } as const;

  const tooltipSlotProps = {
    tooltip: {
      sx: {
        maxWidth: 320,
        display: '-webkit-box',
        WebkitLineClamp: 4,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        wordBreak: 'break-word',
      },
    },
  } as const;

  if (onClick) {
    return (
      <Tooltip title={text} slotProps={tooltipSlotProps}>
        <Typography
          className="table-cell-multiline-clickable"
          sx={{
            ...textStyle,
            fontFamily: osirisTokens.fonts.body,
            letterSpacing: '0.02em',
            color: osirisTokens.text.primary,
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
            cursor: 'pointer',
            '&:hover': {
              color: NAME_HOVER_COLOR,
            },
          }}
          onClick={onClick}
        >
          {text}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={text} slotProps={tooltipSlotProps}>
      <Typography sx={textStyle}>{text}</Typography>
    </Tooltip>
  );
}
